/**
 * useSession.js — lightweight session check for auth-aware CTAs.
 */
import { computed, onMounted, ref } from 'vue';
import {
  ACCOUNT_URL,
  API_URL,
  DASHBOARD_URL,
  LOGIN_URL,
  SIGNUP_URL,
  appPricingUrl,
} from '../config/site.js';

const user = ref(null);
const isLoading = ref(true);
let bootstrapped = false;

async function fetchSession() {
  isLoading.value = true;
  try {
    const res = await fetch(`${API_URL}/api/me`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      user.value = data?.user ?? null;
    } else {
      user.value = null;
    }
  } catch {
    user.value = null;
  } finally {
    isLoading.value = false;
  }
}

export function useSession() {
  onMounted(() => {
    if (!bootstrapped) {
      bootstrapped = true;
      fetchSession();
    }
  });

  const isAuthenticated = computed(() => !!user.value);

  function ctaForPlan(plan, cycle = 'monthly') {
    if (isAuthenticated.value) {
      if (!plan || plan === 'free') {
        return { href: DASHBOARD_URL, label: 'Go to dashboard' };
      }
      return { href: appPricingUrl(plan, cycle), label: 'Choose plan' };
    }
    if (!plan || plan === 'free') {
      return { href: SIGNUP_URL, label: 'Get started free' };
    }
    const sep = SIGNUP_URL.includes('?') ? '&' : '?';
    return {
      href: `${SIGNUP_URL}${sep}plan=${encodeURIComponent(plan)}&cycle=${encodeURIComponent(cycle)}`,
      label: plan === 'core' ? 'Choose Core' : plan === 'pro' ? 'Choose Pro' : 'Choose Terminal',
    };
  }

  return {
    user,
    isLoading,
    isAuthenticated,
    loginUrl: LOGIN_URL,
    signupUrl: SIGNUP_URL,
    dashboardUrl: DASHBOARD_URL,
    accountUrl: ACCOUNT_URL,
    ctaForPlan,
    refresh: fetchSession,
  };
}
